import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { PlanTask } from '../types/today';
import { TaskAvatar } from './TaskAvatar';
import RepeatIcon from '@mui/icons-material/Repeat';
import NotificationsIcon from '@mui/icons-material/Notifications';

interface TaskRowProps {
  task: PlanTask;
  onToggle?: (taskId: string) => void;
}

/**
 * Format time from 24h to 12h format
 * e.g., "07:30" -> "7:30 AM", "14:00" -> "2:00 PM"
 */
function formatTime(time24: string): string {
  const [hours24, minutes] = time24.split(':').map(Number);
  const hours12 = hours24 % 12 || 12;
  const period = hours24 < 12 ? 'AM' : 'PM';
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Format duration in minutes to human-readable string
 * e.g., 25 -> "25 min", 90 -> "1 hr 30 min"
 */
function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

export const TaskRow: React.FC<TaskRowProps> = ({ task, onToggle }) => {
  const handleToggle = () => {
    onToggle?.(task.id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        '&:hover': {
          bgcolor: 'action.hover',
        },
        borderRadius: 1,
        px: 1,
        cursor: 'pointer',
      }}
      onClick={handleToggle}
    >
      {/* Checkbox */}
      <Checkbox
        checked={task.isCompleted || false}
        size="small"
        sx={{
          p: 0,
          mt: 0.5,
          '& .MuiSvgIcon-root': {
            fontSize: 20,
          },
          '&.Mui-checked': {
            color: 'success.main',
          },
        }}
      />

      {/* Task content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title with emoji */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            color: task.isCompleted ? 'text.secondary' : 'text.primary',
            textDecoration: task.isCompleted ? 'line-through' : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {task.title}
          {task.emoji && <span>{task.emoji}</span>}
        </Typography>

        {/* Metadata line */}
        {(task.startTime || task.expectedDurationMinutes || task.isRecurring || task.hasReminder) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.25,
            }}
          >
            {/* Start time in green */}
            {task.startTime && (
              <Typography
                variant="caption"
                sx={{
                  color: 'success.main',
                  fontWeight: 500,
                }}
              >
                {formatTime(task.startTime)}
              </Typography>
            )}

            {/* Separator */}
            {task.startTime && task.expectedDurationMinutes && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mx: 0.25 }}>
                ·
              </Typography>
            )}

            {/* Duration */}
            {task.expectedDurationMinutes && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatDuration(task.expectedDurationMinutes)}
              </Typography>
            )}

            {/* Recurring icon */}
            {task.isRecurring && (
              <RepeatIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  ml: 0.5,
                }}
              />
            )}

            {/* Reminder icon */}
            {task.hasReminder && (
              <NotificationsIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  ml: task.isRecurring ? 0 : 0.5,
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Owner avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', mt: 0.5 }}>
        <TaskAvatar owner={task.owner} size={24} />
      </Box>
    </Box>
  );
};

export default TaskRow;
